import { Currency, CurrencyAmount, Percent, Token } from '@kodiak-finance/sdk-core'
import CustomRecipient from 'components/CustomRecipient'
import PriceImpactWarning from 'components/swap/PriceImpactWarning'
import SwapDetailsDropdown from 'components/swap/SwapDetailsDropdown'
import UnsupportedCurrencyFooter from 'components/swap/UnsupportedCurrencyFooter'
import { ValidationWarning } from 'components/swap/ValidationWarning'
import Switch from 'components/Switch'
import { MouseoverTooltip } from 'components/Tooltip'
import TransactionConfirmationModal, {
  ConfirmationModalContent,
  TransactionErrorContent,
} from 'components/TransactionConfirmationModal'
import useBGTRedeemCallback, { BGTRedeemErrorText, BGTRedeemType } from 'hooks/useBGTRedeemCallback'
import { useChainAwareNavigate } from 'hooks/useChainAwareNavigate'
import { useConnection } from 'hooks/useConnection'
import { useSwapCallback } from 'hooks/useSwapCallback'
import { useGetTokenPrice } from 'hooks/useTokenPrice'
import JSBI from 'jsbi'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import { formatSwapAmount } from 'lib/utils/formatAmount'
import { ArrowUpDown } from 'lucide-react'
import { Context, lazy, Suspense, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import React from 'react'
import { CheckCircle, HelpCircle } from 'react-feather'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Text } from 'rebass'
import { useToggleWalletModal } from 'state/application/hooks'
import { useProviders } from 'state/providers/hooks'
import { TradeState } from 'state/routing/trades'
import styled, { DefaultTheme, ThemeContext } from 'styled-components'
import { getQueryParams, setQueryParams } from 'utils/queryParams'
import { Hash } from 'viem'

import { ButtonConfirmed, ButtonError, ButtonLight, ButtonPrimary } from '../../components/Button'
import { AutoColumn } from '../../components/Column'
import CurrencyInputPanel from '../../components/CurrencyInputPanel'
import CurrencyLogo from '../../components/CurrencyLogo'
import Loader from '../../components/Loader'
import { AutoRow } from '../../components/Row'
import SwapHeader from '../../components/swap/SwapHeader'
import { SwitchLocaleLink } from '../../components/SwitchLocaleLink'
import TokenWarningModal from '../../components/TokenWarningModal'
import { useClearKxQuoteCache } from '../../hooks/backend/useClearKxQuoteCache'
import { useAllTokens, useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallbackFromTrade } from '../../hooks/useApproveCallback'
import { useIsSwapUnsupported } from '../../hooks/useIsSwapUnsupported'
import useWaitForTransactionReceipt from '../../hooks/useWaitForTransactionReceipt'
import useWrapCallback, { WrapErrorText, WrapType } from '../../hooks/useWrapCallback'
import { Field } from '../../state/swap/actions'
import {
  useDefaultsFromURLSearch,
  useDerivedSwapInfo,
  useSwapActionHandlers,
  useSwapState,
} from '../../state/swap/hooks'
import { ThemedText } from '../../theme'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { warningSeverity } from '../../utils/prices'
import { FORM_TYPES, isTwapSupportedChain, TWAP_FORMS } from './consts'
import { OrderHistory } from './twap/twap-orders'

const TwapForm = lazy(() => import('./twap/twap-form'))
const MultiSwapComponent = lazy(() => import('../../components/swap/multiswap/MultiSwapComponent'))

enum SectionName {
  CURRENCY_INPUT_PANEL = 'swap-currency-input',
  CURRENCY_OUTPUT_PANEL = 'swap-currency-output',
}

// ============================
// STYLED COMPONENTS
// ============================
export const SwapWrapper = styled.div`
  display: flex;
  max-width: 448px;
  width: 100%;
  flex-direction: column;
  margin: 0 auto;
  gap: 0.5rem;
  border-radius: 4px;
  backdrop-filter: blur(4px);
  overflow: hidden;
`

export const InputsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`

const InputSection = styled.div``

const AlertWrapper = styled.div`
  max-width: 460px;
  width: 100%;
`

const BottomWrapper = styled.div`
  padding-top: 1rem;
  width: 100%;
`

export const SwapAndChartWrapper = styled.div<{ open: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
  flex-direction: column;
  gap: 1rem;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    padding: 0.5rem;
    justify-content: flex-start;
    padding-top: 2rem;
  `}
`

export const SwapContainer = styled.div`
  display: grid;
  gap: 2rem;
  width: 444px;
  background: ${({ theme }) => theme.cardPrimary};
  border: 2px solid rgba(51, 65, 85, 0.5);
  border-radius: 4px;
  padding: 1rem;

  @media (max-width: 480px) {
    width: calc(100vw - 2rem);
  }
`

export const SwitchButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 10;
  padding: 0.5rem 0;
  width: 100%;
`

export const SwitchButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: ${({ theme }) => theme.cardSecondary};
  border: 2px solid rgb(71, 85, 105);
  border-radius: 4px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
    color: white;
  }
`

export const PercentageButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
`

export const PercentageButton = styled.button<{ disabled?: boolean }>`
  height: 28px;
  padding: 0 0.75rem;
  background-color: ${({ theme }) => theme.cardSecondary};
  border: 1px solid ${({ theme }) => theme.cardBorderSecondary};
  color: ${({ theme }) => theme.textSecondary};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.2s;
  flex: 1;
  min-width: 0;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 11px;
    padding: 0 0.5rem;
  `};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.cardTertiary};
    color: ${({ theme }) => theme.textPrimary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`

type PrimaryFormValue = FORM_TYPES.SWAP | FORM_TYPES.MULTI | 'advanced'

const PRIMARY_FORM_OPTIONS: Array<{ value: PrimaryFormValue; label: string }> = [
  {
    value: FORM_TYPES.SWAP,
    label: 'Swap',
  },
  {
    value: FORM_TYPES.MULTI,
    label: 'Multi',
  },
  {
    value: 'advanced',
    label: 'Advanced',
  },
]

const ADVANCED_FORM_OPTIONS = [
  {
    value: FORM_TYPES.TWAP,
    label: 'TWAP',
  },
  {
    value: FORM_TYPES.LIMIT,
    label: 'Limit',
  },
  {
    value: FORM_TYPES.STOP_LOSS,
    label: 'SL',
  },
  {
    value: FORM_TYPES.TAKE_PROFIT,
    label: 'TP',
  },
]

const FormSelectorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 444px;
  margin: 0 auto;

  @media (max-width: 480px) {
    width: calc(100vw - 2rem);
  }
`

const StyledSwitch = styled.div`
  width: auto;
  display: flex;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  .option {
    padding: 4px 30px;
  }
`

const useActiveFormType = (): FORM_TYPES => {
  const [searchParams] = useSearchParams()
  const { chainId } = useConnection()
  const formType = (searchParams.get('form') as FORM_TYPES) || FORM_TYPES.SWAP
  return !isTwapSupportedChain(chainId) && TWAP_FORMS.includes(formType) ? FORM_TYPES.SWAP : formType
}

const FormSelector = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { chainId } = useConnection()
  const activeForm = useActiveFormType()
  const primaryFormOptions = useMemo(
    () =>
      isTwapSupportedChain(chainId)
        ? PRIMARY_FORM_OPTIONS
        : PRIMARY_FORM_OPTIONS.filter((option) => option.value !== 'advanced'),
    [chainId]
  )
  const activePrimaryForm: PrimaryFormValue =
    activeForm === FORM_TYPES.MULTI ? FORM_TYPES.MULTI : TWAP_FORMS.includes(activeForm) ? 'advanced' : FORM_TYPES.SWAP

  const updateForm = useCallback(
    (form: FORM_TYPES) => {
      // clone existing params
      const newParams = new URLSearchParams(searchParams)
      // update or add the panel param
      newParams.set('form', form)

      navigate(`${location.pathname}?${newParams.toString()}`)
    },
    [navigate, location.pathname, searchParams]
  )

  const onPrimaryClick = useCallback(
    (form: PrimaryFormValue) => {
      if (form === 'advanced') {
        updateForm(TWAP_FORMS.includes(activeForm) ? activeForm : FORM_TYPES.TWAP)
        return
      }

      updateForm(form)
    },
    [activeForm, updateForm]
  )

  return (
    <FormSelectorWrapper>
      <StyledSwitch>
        <Switch
          options={primaryFormOptions}
          activeValue={activePrimaryForm}
          setActiveValue={(it) => onPrimaryClick(it as PrimaryFormValue)}
        />
      </StyledSwitch>

      {activePrimaryForm === 'advanced' && (
        <StyledSwitch>
          <Switch
            options={ADVANCED_FORM_OPTIONS}
            activeValue={activeForm}
            setActiveValue={(it) => updateForm(it as FORM_TYPES)}
          />
        </StyledSwitch>
      )}
    </FormSelectorWrapper>
  )
}

// ============================
// TYPES & ENUMS
// ============================
enum TransactionCategory {
  SWAP = 'SWAP',
  WRAP = 'WRAP',
  UNWRAP = 'UNWRAP',
  BGT_REDEEM = 'BGT_REDEEM',
}

interface TransactionState {
  attemptingTxn: boolean
  errorMessage: string | undefined
  error?: any // Full error object for better error parsing
  txHash: string | undefined
  category: TransactionCategory | undefined
  transactionWaiting?: boolean // for safe multisig execution
}

// ============================
// MAIN COMPONENT
// ============================
export default React.memo(function Swap() {
  // ============================
  // HOOKS & BASIC STATE
  // ============================
  const navigate = useChainAwareNavigate()
  const theme = useContext(ThemeContext as Context<DefaultTheme>)
  const nativeToken = useNativeCurrency()
  const { price: nativePrice } = useGetTokenPrice(nativeToken.wrapped.address)
  const { account } = useConnection()
  const toggleWalletModal = useToggleWalletModal()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()
  const clearQuoteCache = useClearKxQuoteCache()
  const formType = useActiveFormType()
  const isTWAP = useMemo(() => TWAP_FORMS.includes(formType), [formType])
  const isMULTI = formType === FORM_TYPES.MULTI
  // ============================
  // URL & TOKEN LOADING STATE
  // ============================
  const loadedUrlParams = useDefaultsFromURLSearch()
  const [loadedInputCurrency, loadedOutputCurrency] = [
    useCurrency(loadedUrlParams?.[Field.INPUT]?.currencyId),
    useCurrency(loadedUrlParams?.[Field.OUTPUT]?.currencyId),
  ]

  const {
    availableProviders,
    selectedProviders,
    isLoading: isLoadingProviders,
    toggleProviderSelection,
    clearProviderSelection,
    selectAllProviders,
  } = useProviders()

  const [dismissTokenWarning, setDismissTokenWarning] = useState<boolean>(false)
  const urlLoadedTokens: Token[] = useMemo(
    () => [loadedInputCurrency, loadedOutputCurrency]?.filter((c): c is Token => c?.isToken ?? false) ?? [],
    [loadedInputCurrency, loadedOutputCurrency]
  )

  const defaultTokens = useAllTokens()
  const importTokensNotInDefault = useMemo(
    () => urlLoadedTokens && urlLoadedTokens.filter((token: Token) => !(token.address in defaultTokens)),
    [defaultTokens, urlLoadedTokens]
  )

  // ============================
  // TRANSACTION STATE (UNIFIED)
  // ============================
  const [transactionState, setTransactionState] = useState<TransactionState>({
    attemptingTxn: false,
    errorMessage: undefined,
    txHash: undefined,
    category: undefined,
  })

  const [showChart] = useState(false)
  const [showInverted, setShowInverted] = useState<boolean>(false)

  // ============================
  // SWAP STATE & DERIVED INFO
  // ============================
  const { independentField, typedValue, recipient, INPUT: swapStateInput, OUTPUT: swapStateOutput } = useSwapState()
  const {
    onSwitchTokens,
    onCurrencySelection,
    onUserInput,
    onChangeRecipient: _onChangeRecipient,
  } = useSwapActionHandlers()

  useEffect(() => {
    if (loadedUrlParams.canSyncUrl && swapStateInput.currencyId && swapStateOutput.currencyId) {
      const currentParams = getQueryParams()

      if (!currentParams.inputCurrency || !currentParams.outputCurrency) {
        setQueryParams({
          inputCurrency: swapStateInput.currencyId,
          outputCurrency: swapStateOutput.currencyId,
        })
      }
    }
  }, [loadedUrlParams.canSyncUrl, swapStateInput.currencyId, swapStateOutput.currencyId])

  const {
    selectedTrade: trade,
    tradeState,
    allowedSlippage,
    currencyBalances,
    parsedAmount,
    currencies,
    inputError: swapInputError,
    trades,
    isBGTSwap,
    isUnsupportedSwap,
    refetch,
    isRefetching,
    calldataValidation,
  } = useDerivedSwapInfo(
    selectedProviders,
    transactionState.attemptingTxn && transactionState.category === TransactionCategory.SWAP
  )

  // ============================
  // WRAP & BGT REDEEM HOOKS
  // ============================
  const {
    wrapType,
    execute: onWrap,
    inputError: wrapInputError,
    isPending: isWrapPending,
  } = useWrapCallback(currencies[Field.INPUT], currencies[Field.OUTPUT], typedValue)

  const {
    redeemType,
    execute: onBGTRedeem,
    inputError: bgtRedeemInputError,
    isPending: isBGTRedeemPending,
  } = useBGTRedeemCallback(currencies[Field.INPUT], currencies[Field.OUTPUT], typedValue, recipient)

  const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
  const showBGTRedeem: boolean = redeemType !== BGTRedeemType.NOT_APPLICABLE

  // ============================
  // APPROVAL STATE & HANDLERS
  // ============================

  const [approvalState, approveCallback] = useApproveCallbackFromTrade(
    trade,
    allowedSlippage,
    independentField === Field.OUTPUT ? trade?.inputAmount : parsedAmount
  )

  const [hasApprovedInSession, setHasApprovedInSession] = useState<boolean>(false)
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)
  const [approvalAttempting, setApprovalAttempting] = useState<boolean>(false)

  // Reset approval state when input currency changes
  useEffect(() => {
    setApprovalSubmitted(false)
    setApprovalAttempting(false)
    setHasApprovedInSession(false)
  }, [currencies[Field.INPUT]])

  // Track approval state changes
  useEffect(() => {
    if (approvalState === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    }
    if (approvalState === ApprovalState.APPROVED) {
      setApprovalAttempting(false)
      setApprovalSubmitted(false)
    }
  }, [approvalState])

  // ============================
  // SWAP CALLBACK & STATE
  // ============================

  const { callback: swapCallback } = useSwapCallback(trade, allowedSlippage, recipient)

  // const { address: recipientAddress } = useENSAddress(recipient)

  // ============================
  // DERIVED VALUES & CALCULATIONS
  // ============================
  const parsedAmounts = useMemo(
    () =>
      showWrap || showBGTRedeem
        ? {
            [Field.INPUT]: parsedAmount,
            [Field.OUTPUT]: parsedAmount,
          }
        : {
            [Field.INPUT]: independentField === Field.INPUT ? parsedAmount : trade?.inputAmount,
            [Field.OUTPUT]: independentField === Field.OUTPUT ? parsedAmount : trade?.outputAmount,
          },
    [independentField, parsedAmount, showBGTRedeem, showWrap, trade?.inputAmount, trade?.outputAmount]
  )

  const formattedAmounts = useMemo(
    () => ({
      [independentField]: typedValue,
      [independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT]:
        showWrap || showBGTRedeem
          ? parsedAmounts[independentField]?.toExact() ?? ''
          : selectedProviders.length === 0
          ? ''
          : formatSwapAmount(parsedAmounts[independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT]) ?? '',
    }),
    [independentField, parsedAmounts, selectedProviders, showBGTRedeem, showWrap, typedValue]
  )

  const userHasSpecifiedInputOutput = Boolean(
    currencies[Field.INPUT] && currencies[Field.OUTPUT] && parsedAmounts[independentField]?.greaterThan(JSBI.BigInt(0))
  )

  const maxInputAmount: CurrencyAmount<Currency> | undefined = useMemo(
    () => maxAmountSpend(currencyBalances[Field.INPUT]),
    [currencyBalances]
  )

  // Route state calculations
  const [routeNotFound, routeIsLoading, routeIsSyncing, isStaleQuote] = useMemo(() => {
    if (isBGTSwap || isUnsupportedSwap) return [false, false, false, false]

    const isLoadingState = TradeState.LOADING === tradeState
    const isSyncingState = TradeState.SYNCING === tradeState && selectedProviders.length > 0
    const isStaleState = TradeState.STALE === tradeState

    const shouldShowNoRoute =
      (tradeState === TradeState.NO_ROUTE_FOUND || (!trade && tradeState === TradeState.VALID)) &&
      !isLoadingState &&
      !isSyncingState &&
      !isRefetching &&
      userHasSpecifiedInputOutput

    return [shouldShowNoRoute, isLoadingState || isRefetching, isSyncingState, isStaleState]
  }, [
    isBGTSwap,
    isUnsupportedSwap,
    trade,
    tradeState,
    isRefetching,
    userHasSpecifiedInputOutput,
    selectedProviders.length,
  ])

  const [debouncedRouteNotFound, setDebouncedRouteNotFound] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedRouteNotFound(routeNotFound), 1000)
    return () => clearTimeout(timer)
  }, [routeNotFound])

  const { priceImpactSeverity, largerPriceImpact } = useMemo(() => {
    if (routeIsLoading || routeIsSyncing || isRefetching || tradeState !== TradeState.VALID || !trade) {
      return { priceImpactSeverity: 0, largerPriceImpact: undefined }
    }

    return {
      priceImpactSeverity: warningSeverity(trade.priceImpact ?? null),
      largerPriceImpact: trade.priceImpact,
    }
  }, [routeIsLoading, routeIsSyncing, isRefetching, tradeState, trade])

  // USD value calculations
  const calculateUSDValue = useCallback(
    (amount: string | undefined): string | undefined => {
      if (!amount || !nativePrice) return undefined
      const numAmount = parseFloat(amount)
      if (isNaN(numAmount)) return undefined
      return (nativePrice * numAmount).toString()
    },
    [nativePrice]
  )

  const getInputUSDValue = useMemo((): string | undefined => {
    if (!showWrap && !showBGTRedeem && trade?.inputAmountUSD) {
      return trade.inputAmountUSD
    }
    if ((showWrap || showBGTRedeem || isBGTSwap) && formattedAmounts[Field.INPUT]) {
      return calculateUSDValue(formattedAmounts[Field.INPUT])
    }
    return undefined
  }, [showWrap, showBGTRedeem, isBGTSwap, trade?.inputAmountUSD, formattedAmounts, calculateUSDValue])

  const getOutputUSDValue = useMemo((): string | undefined => {
    if (!showWrap && !showBGTRedeem && trade?.outputAmountUSD && selectedProviders.length > 0) {
      return trade.outputAmountUSD
    }
    if ((showWrap || showBGTRedeem || isBGTSwap) && formattedAmounts[Field.OUTPUT]) {
      return calculateUSDValue(formattedAmounts[Field.OUTPUT])
    }
    return undefined
  }, [
    showWrap,
    showBGTRedeem,
    isBGTSwap,
    trade?.outputAmountUSD,
    formattedAmounts,
    calculateUSDValue,
    selectedProviders.length,
  ])

  // ============================
  // BUSINESS LOGIC & CONDITIONS
  // ============================
  const swapIsUnsupported = useIsSwapUnsupported(currencies[Field.INPUT], currencies[Field.OUTPUT])
  const isValid = !swapInputError && selectedProviders.length > 0

  const shouldShowPriceImpactWarning =
    !showWrap &&
    !showBGTRedeem &&
    ((largerPriceImpact && priceImpactSeverity === 5) || // High impact warning
      largerPriceImpact === null) // Unknown impact warning

  const hasValidationErrors = useMemo(() => calldataValidation && !calldataValidation.isValid, [calldataValidation])

  const showApproveFlow = useMemo(() => {
    if (
      isBGTSwap ||
      isUnsupportedSwap ||
      !account ||
      swapInputError ||
      !currencies[Field.INPUT]?.isToken ||
      !parsedAmounts[Field.INPUT]
    ) {
      return false
    }
    if (currencies[Field.INPUT]?.isNative || showWrap) return false

    const routesDefinitelyFailed = debouncedRouteNotFound && !routeIsLoading && !routeIsSyncing && !isRefetching
    if (routesDefinitelyFailed || approvalState === ApprovalState.UNKNOWN) return false

    return (
      approvalState === ApprovalState.NOT_APPROVED ||
      approvalState === ApprovalState.PENDING ||
      approvalAttempting ||
      (approvalState === ApprovalState.APPROVED && hasApprovedInSession)
    )
  }, [
    isBGTSwap,
    isUnsupportedSwap,
    account,
    swapInputError,
    currencies,
    parsedAmounts,
    showWrap,
    debouncedRouteNotFound,
    routeIsLoading,
    routeIsSyncing,
    isRefetching,
    approvalState,
    approvalAttempting,
    hasApprovedInSession,
  ])

  // Button state calculations
  const isApprovalProcessing = useMemo(
    () =>
      approvalAttempting ||
      approvalState === ApprovalState.PENDING ||
      (approvalSubmitted && approvalState === ApprovalState.NOT_APPROVED),
    [approvalAttempting, approvalState, approvalSubmitted]
  )

  const isApprovalCompleted = useMemo(() => approvalState === ApprovalState.APPROVED, [approvalState])

  const swapButtonDisabled = useMemo(() => {
    if (!isValid || !trade || !trade.canExecute || !swapCallback) return true
    if (routeIsSyncing || routeIsLoading || isRefetching) return true
    if (transactionState.attemptingTxn) return true
    if (hasValidationErrors) return true

    if (showApproveFlow) {
      if (approvalAttempting) return true
      if (approvalState !== ApprovalState.APPROVED) return true
    }
    return false
  }, [
    isValid,
    trade,
    swapCallback,
    routeIsSyncing,
    routeIsLoading,
    isRefetching,
    transactionState.attemptingTxn,
    hasValidationErrors,
    showApproveFlow,
    approvalAttempting,
    approvalState,
  ])

  // ============================
  // TRANSACTION HANDLERS
  // ============================
  const getTransactionDetails = useCallback((): {
    title: string
    pendingText: string
    successText: string
    analyticsCategory: string
  } => {
    const wrapped = nativeToken?.wrapped

    switch (transactionState.category) {
      case TransactionCategory.WRAP:
        return {
          title: 'Confirm Wrap',
          pendingText: `Wrapping ${parsedAmounts[Field.INPUT]?.toSignificant(6)} ${
            nativeToken?.symbol
          } to ${parsedAmounts[Field.INPUT]?.toSignificant(6)} ${wrapped?.symbol}`,
          successText: `Wrapping ${parsedAmounts[Field.INPUT]?.toSignificant(6)} ${
            nativeToken?.symbol
          } to ${parsedAmounts[Field.INPUT]?.toSignificant(6)} ${wrapped?.symbol}`,
          analyticsCategory: 'Wrap',
        }
      case TransactionCategory.UNWRAP:
        return {
          title: 'Confirm Unwrap',
          pendingText: `Unwrapping ${parsedAmounts[Field.INPUT]?.toSignificant(6)} ${
            wrapped?.symbol
          } to ${parsedAmounts[Field.INPUT]?.toSignificant(6)} ${nativeToken?.symbol}`,
          successText: `Unwrapping ${parsedAmounts[Field.INPUT]?.toSignificant(6)} ${
            wrapped?.symbol
          } to ${parsedAmounts[Field.INPUT]?.toSignificant(6)} ${nativeToken?.symbol}`,
          analyticsCategory: 'Unwrap',
        }
      case TransactionCategory.BGT_REDEEM:
        return {
          title: 'Confirm BGT Redemption',
          pendingText: `Redeeming ${parsedAmounts[Field.INPUT]?.toSignificant(6)} BGT for ${parsedAmounts[
            Field.INPUT
          ]?.toSignificant(6)} BERA`,
          successText: `Redeeming ${parsedAmounts[Field.INPUT]?.toSignificant(6)} BGT for ${parsedAmounts[
            Field.INPUT
          ]?.toSignificant(6)} BERA`,
          analyticsCategory: 'BGT Redeem',
        }
      default: // SWAP
        return {
          title: 'Confirm Swap',
          pendingText: `Swapping ${trade?.inputAmount?.toSignificant(6)} ${
            trade?.inputAmount?.currency?.symbol
          } for ${trade?.outputAmount?.toSignificant(6)} ${trade?.outputAmount?.currency?.symbol}`,
          successText: `Swapping ${trade?.inputAmount?.toSignificant(6)} ${
            trade?.inputAmount?.currency?.symbol
          } for ${trade?.outputAmount?.toSignificant(6)} ${trade?.outputAmount?.currency?.symbol}`,
          analyticsCategory: 'Swap',
        }
    }
  }, [
    nativeToken?.wrapped,
    nativeToken?.symbol,
    transactionState.category,
    parsedAmounts,
    trade?.inputAmount,
    trade?.outputAmount,
  ])

  const handleTransaction = useCallback(
    async (
      executeFunction: () => Promise<string>,
      category: TransactionCategory,
      fallbackErrorMessage: string
    ): Promise<void> => {
      if (transactionState.attemptingTxn) return

      setTransactionState({
        attemptingTxn: true,
        errorMessage: undefined,
        txHash: undefined,
        category,
        transactionWaiting: false,
      })

      try {
        const hash: string = await executeFunction()

        setTransactionState({
          attemptingTxn: false,
          errorMessage: undefined,
          txHash: hash,
          category,
          transactionWaiting: true,
        })

        await waitForTransactionReceipt({ hash: hash as Hash })

        setTransactionState((prev) => ({
          ...prev,
          transactionWaiting: false,
        }))

        onUserInput(Field.INPUT, '')
      } catch (error: any) {
        const reason: string = error?.message || error?.reason || fallbackErrorMessage

        setTransactionState({
          attemptingTxn: false,
          errorMessage: reason === 'execution reverted' ? fallbackErrorMessage : reason,
          error, // Store full error object for better parsing
          txHash: undefined,
          category,
          transactionWaiting: false,
        })
      }
    },
    [transactionState.attemptingTxn, onUserInput, getTransactionDetails, waitForTransactionReceipt]
  )

  const handleWrap = useCallback(async (): Promise<void> => {
    if (!onWrap || isWrapPending) return

    const category = wrapType === WrapType.WRAP ? TransactionCategory.WRAP : TransactionCategory.UNWRAP
    const errorMessage = wrapType === WrapType.WRAP ? 'Wrap failed' : 'Unwrap failed'

    await handleTransaction(onWrap, category, errorMessage)
  }, [onWrap, isWrapPending, wrapType, handleTransaction])

  const handleBGTRedeem = useCallback(async (): Promise<void> => {
    if (!onBGTRedeem || isBGTRedeemPending) return
    await handleTransaction(onBGTRedeem, TransactionCategory.BGT_REDEEM, 'BGT redemption failed')
  }, [onBGTRedeem, isBGTRedeemPending, handleTransaction])

  const handleSwap = useCallback(async (): Promise<void> => {
    if (!swapCallback || !trade || transactionState.attemptingTxn) return
    if (showApproveFlow && approvalState !== ApprovalState.APPROVED) return

    setTransactionState({
      attemptingTxn: true,
      errorMessage: undefined,
      txHash: undefined,
      category: TransactionCategory.SWAP,
      transactionWaiting: false,
    })

    try {
      const hash = await swapCallback()

      setTransactionState({
        attemptingTxn: false,
        errorMessage: undefined,
        txHash: hash,
        category: TransactionCategory.SWAP,
        transactionWaiting: true,
      })

      await waitForTransactionReceipt({ hash: hash as Hash })
      try {
        await clearQuoteCache()
      } catch (cacheError) {
        console.warn('Failed to clear quote cache after swap:', cacheError)
      }

      setTransactionState((prev) => ({
        ...prev,
        transactionWaiting: false,
      }))

      onUserInput(Field.INPUT, '')
    } catch (error: any) {
      const reason = error?.message || error?.reason || 'Swap failed'

      setTransactionState({
        attemptingTxn: false,
        errorMessage: reason === 'execution reverted' ? 'Swap failed' : reason,
        error, // Store full error object for better parsing
        txHash: undefined,
        category: TransactionCategory.SWAP,
        transactionWaiting: false,
      })
    }
  }, [
    swapCallback,
    trade,
    transactionState.attemptingTxn,
    showApproveFlow,
    approvalState,
    onUserInput,
    clearQuoteCache,
    waitForTransactionReceipt,
  ])

  const handleApprove = useCallback(async () => {
    {
      try {
        setApprovalAttempting(true)
        setApprovalSubmitted(true)
        await approveCallback()
        setHasApprovedInSession(true)
      } catch {
        setApprovalAttempting(false)
        setApprovalSubmitted(false)
      }
    }
  }, [approveCallback])

  // ============================
  // INPUT HANDLERS
  // ============================
  const handleTypeInput = useCallback(
    (value: string) => {
      const decimalIndex = value.indexOf('.')
      if (decimalIndex !== -1 && value.length - decimalIndex - 1 > 18) {
        value = value.substring(0, decimalIndex + 19)
      }
      onUserInput(Field.INPUT, value)
    },
    [onUserInput]
  )

  const handleInputSelect = useCallback(
    (inputCurrency: Currency) => {
      setApprovalSubmitted(false)
      onCurrencySelection(Field.INPUT, inputCurrency)
    },
    [onCurrencySelection]
  )

  const handleOutputSelect = useCallback(
    (outputCurrency: Currency) => onCurrencySelection(Field.OUTPUT, outputCurrency),
    [onCurrencySelection]
  )

  const handleMaxInput = useCallback(() => {
    if (maxInputAmount) {
      onUserInput(Field.INPUT, maxInputAmount.toExact())
    }
  }, [maxInputAmount, onUserInput])

  const handlePercentageInput = useCallback(
    (percentage: number) => {
      if (maxInputAmount) {
        const amount = maxInputAmount.multiply(new Percent(percentage, 100))
        onUserInput(Field.INPUT, formatSwapAmount(amount))
      }
    },
    [maxInputAmount, onUserInput]
  )

  const onChangeRecipient = useCallback(
    (recipient: string | null) => {
      _onChangeRecipient(recipient)
      setTimeout(() => refetch(), 100)
    },
    [_onChangeRecipient, refetch]
  )

  // ============================
  // MODAL HANDLERS
  // ============================
  const handleStatusModalDismiss = useCallback(() => {
    setTransactionState({
      attemptingTxn: false,
      errorMessage: undefined,
      txHash: undefined,
      category: undefined,
    })
  }, [])

  const handleConfirmTokenWarning = useCallback(() => setDismissTokenWarning(true), [])

  const handleDismissTokenWarning = useCallback(() => {
    setDismissTokenWarning(true)
    navigate('/swap/')
  }, [navigate])

  // ============================
  // MODAL CONTENT
  // ============================
  const confirmationContent = useCallback(() => {
    if (transactionState.errorMessage) {
      return (
        <TransactionErrorContent
          onDismiss={handleStatusModalDismiss}
          message={transactionState.errorMessage}
          error={transactionState.error}
        />
      )
    }

    if (transactionState.txHash) {
      const details = getTransactionDetails()

      return (
        <ConfirmationModalContent
          title={<>{details.title}</>}
          onDismiss={handleStatusModalDismiss}
          topContent={() => (
            <div>
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <div style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>Transaction Submitted</div>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>{details.successText}</div>
              </div>
            </div>
          )}
          bottomContent={() => null}
        />
      )
    }

    return null
  }, [
    transactionState.errorMessage,
    transactionState.error,
    transactionState.txHash,
    handleStatusModalDismiss,
    getTransactionDetails,
  ])

  const pendingText = useMemo(() => {
    const details = getTransactionDetails()
    return <>{details.pendingText}</>
  }, [getTransactionDetails])

  // ============================
  // BUTTON TEXT & STATE
  // ============================

  const getApprovalButtonContent = () => {
    if (isApprovalProcessing) {
      return <>Approving {currencies[Field.INPUT]?.symbol}...</>
    }
    if (isApprovalCompleted) {
      return <>You can now trade {currencies[Field.INPUT]?.symbol}</>
    }
    return <>Allow Kodiak to use your {currencies[Field.INPUT]?.symbol}</>
  }

  const getSwapButtonText = () => {
    if (isBGTSwap) return <>BGT can only be redeemed for BERA</>
    if (swapInputError) return swapInputError

    if (
      approvalState === ApprovalState.UNKNOWN &&
      !approvalSubmitted &&
      !approvalAttempting &&
      currencies[Field.INPUT]?.isToken &&
      userHasSpecifiedInputOutput
    ) {
      return <>Checking Approval...</>
    }

    if (routeIsSyncing || routeIsLoading) return <>Finding best route...</>
    if (priceImpactSeverity > 2) return <>Swap Anyway</>
    if (selectedProviders.length === 0) return <>No provider was selected</>
    return <>Swap</>
  }

  const isDisabledPercentageButtons = !currencies?.INPUT

  const title = useMemo(() => {
    if (formType === FORM_TYPES.TWAP) return 'TWAP'
    if (formType === FORM_TYPES.LIMIT) return 'Limit'
    if (formType === FORM_TYPES.STOP_LOSS) return 'Stop Loss'
    if (formType === FORM_TYPES.TAKE_PROFIT) return 'Take Profit'
    return 'Swap'
  }, [formType])

  // ============================
  // RENDER
  // ============================

  return (
    <>
      {/* Token Warning Modals */}
      <TokenWarningModal
        isOpen={defaultTokens.length && importTokensNotInDefault.length > 0 && !dismissTokenWarning}
        tokens={importTokensNotInDefault}
        onConfirm={handleConfirmTokenWarning}
        onDismiss={handleDismissTokenWarning}
      />

      <SwapAndChartWrapper open={showChart}>
        <FormSelector />
        {isMULTI ? (
          <Suspense>
            <MultiSwapComponent
              providersConfig={{
                isLoading: isLoadingProviders,
                availableProviders,
                selectedProviders,
                onSelect: toggleProviderSelection,
                onReset: clearProviderSelection,
                onSelectAll: selectAllProviders,
              }}
            />
          </Suspense>
        ) : (
          <SwapContainer>
            <SwapHeader
              isTwap={isTWAP}
              allowedSlippage={allowedSlippage}
              buttons={<OrderHistory />}
              title={title}
              onRefresh={refetch}
              providersConfig={{
                isLoading: isLoadingProviders,
                availableProviders,
                selectedProviders,
                onSelect: toggleProviderSelection,
                onReset: clearProviderSelection,
                onSelectAll: selectAllProviders,
              }}
            />

            {isTWAP ? (
              <SwapWrapper id="swap-page">
                <Suspense>
                  <TwapForm
                    swapIsUnsupported={swapIsUnsupported}
                    serverPriceImpact={largerPriceImpact ?? undefined}
                    formType={formType}
                    getInputUSDValue={getInputUSDValue}
                    handleMaxInput={handleMaxInput}
                    handlePercentageInput={handlePercentageInput}
                    selectedTrade={trade}
                    tradeState={tradeState}
                    currencies={currencies}
                    currencyBalances={currencyBalances}
                    showPriceImpactWarning={shouldShowPriceImpactWarning}
                    largerPriceImpact={largerPriceImpact ?? null}
                    priceImpactSeverity={priceImpactSeverity}
                  />
                </Suspense>
              </SwapWrapper>
            ) : (
              <SwapWrapper id="swap-page">
                {/* Transaction Confirmation Modal */}
                <TransactionConfirmationModal
                  isOpen={
                    transactionState.attemptingTxn ||
                    transactionState.transactionWaiting ||
                    !!transactionState.txHash ||
                    !!transactionState.errorMessage
                  }
                  onDismiss={handleStatusModalDismiss}
                  attemptingTxn={transactionState.attemptingTxn}
                  hash={transactionState.txHash}
                  content={confirmationContent}
                  pendingText={pendingText}
                  currencyToAdd={currencies[Field.OUTPUT]?.wrapped}
                  transactionWaiting={transactionState.transactionWaiting}
                />
                {/* Input Section */}
                <InputsWrapper>
                  <InputSection>
                    <CurrencyInputPanel
                      label={
                        independentField === Field.OUTPUT && !showWrap && !showBGTRedeem ? (
                          <>From (at most)</>
                        ) : (
                          <>From</>
                        )
                      }
                      value={formattedAmounts[Field.INPUT]}
                      hideBorder={true}
                      showMaxButton={false}
                      currency={currencies[Field.INPUT] ?? null}
                      onUserInput={handleTypeInput}
                      onMax={handleMaxInput}
                      apiUSDValue={getInputUSDValue}
                      onCurrencySelect={handleInputSelect}
                      otherCurrency={currencies[Field.OUTPUT]}
                      showCommonBases={true}
                      id={SectionName.CURRENCY_INPUT_PANEL}
                      loading={
                        (!showWrap && !showBGTRedeem && independentField === Field.OUTPUT && routeIsSyncing) ||
                        isRefetching
                      }
                      containerProps={{
                        style: {
                          minHeight: '93px',
                        },
                      }}
                    />
                  </InputSection>

                  {/* Switch Button & Percentage Controls */}
                  <SwitchButtonContainer>
                    <PercentageButtonsRow>
                      <PercentageButton
                        disabled={isDisabledPercentageButtons}
                        onClick={() => handlePercentageInput(10)}
                      >
                        10%
                      </PercentageButton>
                      <PercentageButton
                        disabled={isDisabledPercentageButtons}
                        onClick={() => handlePercentageInput(25)}
                      >
                        25%
                      </PercentageButton>
                    </PercentageButtonsRow>
                    <SwitchButton onClick={onSwitchTokens}>
                      <ArrowUpDown />
                    </SwitchButton>
                    <PercentageButtonsRow>
                      <PercentageButton
                        disabled={isDisabledPercentageButtons}
                        onClick={() => handlePercentageInput(50)}
                      >
                        50%
                      </PercentageButton>
                      <PercentageButton disabled={isDisabledPercentageButtons} onClick={handleMaxInput}>
                        MAX
                      </PercentageButton>
                    </PercentageButtonsRow>
                  </SwitchButtonContainer>

                  <InputSection>
                    <CurrencyInputPanel
                      value={formattedAmounts[Field.OUTPUT]}
                      disabled
                      label={
                        independentField === Field.INPUT && !showWrap && !showBGTRedeem ? <>To (at least)</> : <>To</>
                      }
                      showMaxButton={false}
                      hideBalance={false}
                      hideBorder={true}
                      apiUSDValue={getOutputUSDValue}
                      priceImpact={largerPriceImpact ?? undefined}
                      currency={currencies[Field.OUTPUT] ?? null}
                      onCurrencySelect={handleOutputSelect}
                      otherCurrency={currencies[Field.INPUT]}
                      showCommonBases={true}
                      id={SectionName.CURRENCY_OUTPUT_PANEL}
                      loading={
                        (!showWrap && !showBGTRedeem && independentField === Field.INPUT && routeIsSyncing) ||
                        isRefetching
                      }
                      containerProps={{
                        style: {
                          minHeight: '93px',
                        },
                      }}
                    />
                  </InputSection>
                </InputsWrapper>
                {/* Swap Details */}
                {!showWrap &&
                  !showBGTRedeem &&
                  !isBGTSwap &&
                  !isUnsupportedSwap &&
                  userHasSpecifiedInputOutput &&
                  selectedProviders.length > 0 &&
                  (trade || routeIsLoading || routeIsSyncing) && (
                    <SwapDetailsDropdown
                      trade={trade}
                      syncing={routeIsSyncing}
                      loading={routeIsLoading || isRefetching || tradeState === TradeState.SYNCING}
                      showInverted={showInverted}
                      setShowInverted={setShowInverted}
                      allowedSlippage={allowedSlippage}
                      trades={trades}
                      selectedTrade={trade}
                      priceImpact={largerPriceImpact ?? undefined}
                    />
                  )}
                {/* Warnings */}
                {shouldShowPriceImpactWarning && <PriceImpactWarning priceImpact={largerPriceImpact} />}
                {hasValidationErrors && (
                  <AlertWrapper>
                    {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
                    <ValidationWarning validation={calldataValidation!} />
                  </AlertWrapper>
                )}
                {/* Action Buttons */}
                <BottomWrapper>
                  {swapIsUnsupported ? (
                    <ButtonPrimary disabled>
                      <ThemedText.DeprecatedMain mb="4px">Unsupported Asset</ThemedText.DeprecatedMain>
                    </ButtonPrimary>
                  ) : !account ? (
                    <ButtonLight onClick={toggleWalletModal}>Connect Wallet</ButtonLight>
                  ) : showWrap ? (
                    <ButtonPrimary
                      disabled={Boolean(wrapInputError) || isWrapPending || transactionState.attemptingTxn}
                      onClick={handleWrap}
                    >
                      {wrapInputError ? (
                        <WrapErrorText wrapInputError={wrapInputError} />
                      ) : wrapType === WrapType.WRAP ? (
                        'Wrap'
                      ) : (
                        'Unwrap'
                      )}
                    </ButtonPrimary>
                  ) : showBGTRedeem ? (
                    <ButtonPrimary
                      disabled={Boolean(bgtRedeemInputError) || isBGTRedeemPending || transactionState.attemptingTxn}
                      onClick={handleBGTRedeem}
                    >
                      {bgtRedeemInputError ? (
                        <BGTRedeemErrorText bgtRedeemInputError={bgtRedeemInputError} />
                      ) : (
                        'Redeem'
                      )}
                    </ButtonPrimary>
                  ) : ((routeIsLoading || routeIsSyncing || isRefetching) &&
                      !showApproveFlow &&
                      !isBGTSwap &&
                      !isUnsupportedSwap) ||
                    (currencies[Field.INPUT]?.isToken && approvalState === ApprovalState.UNKNOWN && !isBGTSwap) ? (
                    <ButtonPrimary disabled>
                      <Text fontSize={16} fontWeight={500}>
                        {currencies[Field.INPUT]?.isToken &&
                        approvalState === ApprovalState.UNKNOWN &&
                        userHasSpecifiedInputOutput
                          ? 'Checking Approval...'
                          : routeIsLoading
                          ? 'Finding best route...'
                          : isRefetching
                          ? 'Refreshing quotes...'
                          : !userHasSpecifiedInputOutput
                          ? 'Enter an amount'
                          : 'Swap'}
                      </Text>
                    </ButtonPrimary>
                  ) : isStaleQuote && userHasSpecifiedInputOutput ? (
                    <ButtonPrimary disabled>Stale quote. Retry shortly.</ButtonPrimary>
                  ) : debouncedRouteNotFound &&
                    userHasSpecifiedInputOutput &&
                    !routeIsLoading &&
                    !routeIsSyncing &&
                    !isBGTSwap &&
                    !isUnsupportedSwap &&
                    selectedProviders.length > 0 ? (
                    <ButtonPrimary disabled>Insufficient liquidity for this trade.</ButtonPrimary>
                  ) : (
                    <AutoRow style={{ flexWrap: 'nowrap', width: '100%' }}>
                      <AutoColumn style={{ width: '100%' }} gap="12px">
                        {/* Approval Button */}
                        {showApproveFlow && currencies[Field.INPUT]?.isToken && (
                          <ButtonConfirmed
                            onClick={handleApprove}
                            disabled={isApprovalProcessing || isApprovalCompleted}
                            width="100%"
                            altDisabledStyle={approvalState === ApprovalState.PENDING || approvalAttempting}
                            confirmed={isApprovalCompleted}
                          >
                            <AutoRow
                              justify="space-between"
                              style={{ flexWrap: 'nowrap', justifyContent: 'center', gap: '8px' }}
                            >
                              <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <CurrencyLogo
                                  currency={currencies[Field.INPUT]}
                                  size="20px"
                                  style={{ marginRight: '8px', flexShrink: 0 }}
                                />
                                {getApprovalButtonContent()}
                              </span>
                              {isApprovalProcessing ? (
                                <Loader stroke="white" />
                              ) : isApprovalCompleted ? (
                                <CheckCircle size="20" color={theme.deprecated_green1} />
                              ) : (
                                <MouseoverTooltip
                                  text={
                                    <>
                                      You must give the Kodiak smart contracts permission to use your{' '}
                                      {currencies[Field.INPUT]?.symbol}. You only have to do this once per token.
                                    </>
                                  }
                                >
                                  <HelpCircle size="20" color="deprecated_white" style={{ marginLeft: '8px' }} />
                                </MouseoverTooltip>
                              )}
                            </AutoRow>
                          </ButtonConfirmed>
                        )}

                        {/* Swap Button */}
                        <ButtonError
                          onClick={handleSwap}
                          width="100%"
                          id="swap-button"
                          disabled={swapButtonDisabled}
                          error={isValid && priceImpactSeverity > 2}
                        >
                          <Text fontSize={16} fontWeight={500}>
                            {getSwapButtonText()}
                          </Text>
                        </ButtonError>
                      </AutoColumn>
                    </AutoRow>
                  )}

                  {/* Custom Recipient */}
                  {!showWrap && (
                    <CustomRecipient recipient={recipient} onChangeRecipient={onChangeRecipient} account={account} />
                  )}
                </BottomWrapper>
              </SwapWrapper>
            )}
          </SwapContainer>
        )}
      </SwapAndChartWrapper>

      <AlertWrapper />
      <SwitchLocaleLink />
      {swapIsUnsupported && (
        <UnsupportedCurrencyFooter
          show={swapIsUnsupported}
          currencies={[currencies[Field.INPUT], currencies[Field.OUTPUT]]}
        />
      )}
    </>
  )
})
